/**
 * UniverseReviewPane: review mode for proposed facts (Figma 04 Universe,
 * `Universe / Review pane` 12:14304, `Review · Phone` 12:14390).
 *
 * On the desk it is a full-height pane over the rail; the orbit stays live
 * underneath and marks where the current fact lands. On a phone it is the
 * floating sheet of proposal cards. Every decision is optimistic: the fact
 * moves to Done at once, and only a decision the server refuses comes back,
 * with a toast.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Modal, ModalOverlay, Pressable } from "react-aria-components";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Info, MoreHorizontal, Pencil, Sparkles, X } from "lucide-react";
import { queue } from "@/components/Toast";
import { cn } from "@/lib/utils";
import type { FactProposal } from "@/types/chat.type";
import { useDecideBrandFactProposals } from "../api/useDecideBrandFactProposals";
import { useEditFactProposal } from "../api/useEditFactProposal";
import { brandUniversePendingFactProposalsKey } from "../api/useBrandUniversePendingFactProposals";
import type { ProposalPlacement } from "../data/proposalPlacement";
import { ConceptChipPicker, type ConceptChipOption } from "./ConceptChipPicker";
import { useCoShellPortal } from "../CoShellPortalContext";
import { KitIconButton } from "./kit/IconButton";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "./kit/Menu";
import { KitPopover } from "./kit/Popover";
import { useSheetExit } from "./UniverseFactCard";

type Mark = "accept" | "decline";
type Decided = { proposal: FactProposal; mark: Mark };

type Props = {
  brandId: string;
  pending: FactProposal[];
  concepts: ConceptChipOption[];
  placementOf: (proposal: FactProposal) => ProposalPlacement;
  compact: boolean;
  /** The fixture harness: decisions and edits are toasts, nothing is sent. */
  preview?: boolean;
  onClose: () => void;
  /** The fact on top of the pile, so the stage can mark where it lands. */
  onCurrentChange?: (proposal: FactProposal | null) => void;
  /** Closes review; the assistant revises the fact in the chat. */
  onAskAiToRevise: (proposal: FactProposal, instruction: string) => void;
};

const titleOf = (p: FactProposal) => p.factName?.trim() || p.factText;
const bodyOf = (p: FactProposal) => (p.factName?.trim() ? p.factDescription?.trim() || "" : "");

export default function UniverseReviewPane({
  brandId,
  pending,
  concepts,
  placementOf,
  compact,
  preview,
  onClose,
  onCurrentChange,
  onAskAiToRevise,
}: Props) {
  const decide = useDecideBrandFactProposals(brandId);
  const [decided, setDecided] = useState<Decided[]>([]);
  const decidedIds = useMemo(() => new Set(decided.map((d) => d.proposal.id)), [decided]);
  const open = useMemo(() => pending.filter((p) => !decidedIds.has(p.id)), [pending, decidedIds]);
  const current = open[0] ?? null;
  const upNext = open.slice(1);
  const total = open.length + decided.length;
  const paneRef = usePaneFrame(!compact);

  const currentRef = useRef(onCurrentChange);
  useEffect(() => {
    currentRef.current = onCurrentChange;
  }, [onCurrentChange]);
  useEffect(() => {
    currentRef.current?.(compact ? null : current);
  }, [current, compact]);
  useEffect(() => () => currentRef.current?.(null), []);

  // The last decision ends review mode.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (open.length === 0) closeRef.current();
  }, [open.length]);

  const run = (targets: FactProposal[], mark: Mark) => {
    if (targets.length === 0) return;
    setDecided((prev) => [...prev, ...targets.map((proposal) => ({ proposal, mark }))]);
    if (preview) {
      queue.add({ title: `Preview: ${targets.length} ${mark === "accept" ? "accepted" : "declined"}, nothing was sent`, variant: "info" });
      return;
    }
    const restore = (ids: Set<string>) => setDecided((prev) => prev.filter((d) => !ids.has(d.proposal.id)));
    decide.mutate(
      { proposals: targets, decisions: targets.map((p) => ({ proposalId: p.id, decision: mark })) },
      {
        onSuccess: (results) => {
          const failed = new Set(results.filter((r) => r.error).map((r) => r.proposalId));
          if (failed.size === 0) return;
          restore(failed);
          queue.add({ title: "Couldn't save that decision", description: "It is back in the review. Try again.", variant: "error" });
        },
        onError: () => {
          restore(new Set(targets.map((p) => p.id)));
          queue.add({ title: "Couldn't save that decision", description: "It is back in the review. Try again.", variant: "error" });
        },
      },
    );
  };

  const askAi = (proposal: FactProposal, instruction: string) => {
    onClose();
    onAskAiToRevise(proposal, instruction);
  };

  if (compact) {
    return (
      <ReviewSheet
        open={open}
        onClose={onClose}
        onDecideAll={(mark) => run(open, mark)}
        renderCard={(p) => (
          <PhoneProposal
            key={p.id}
            brandId={brandId}
            proposal={p}
            concepts={concepts}
            placement={placementOf(p)}
            preview={preview}
            onDecide={(mark) => run([p], mark)}
            onAskAi={(text) => askAi(p, text)}
          />
        )}
      />
    );
  }

  return (
    <aside
      ref={paneRef}
      className="co-review-pane"
      aria-label="Review proposed facts"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="co-review-head">
        <h2 className="co-review-title">Review proposed facts</h2>
        <KitIconButton icon={X} kind="close" size="lg" aria-label="Close review" onClick={onClose} autoFocus />
      </div>
      <p className="co-review-note">Accept or decline each fact. Decisions apply immediately.</p>
      <div className="co-review-progress">
        <span className="co-review-step">
          {Math.min(decided.length + 1, total)} of {total}
        </span>
        <span className="co-review-bars" aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <span key={i} data-on={i <= decided.length || undefined} />
          ))}
        </span>
      </div>

      {current ? (
        <ProposalCard
          key={current.id}
          brandId={brandId}
          proposal={current}
          concepts={concepts}
          placement={placementOf(current)}
          preview={preview}
          onDecide={(mark) => run([current], mark)}
          onAskAi={(text) => askAi(current, text)}
        />
      ) : null}

      <div className="co-review-lists">
        {upNext.length > 0 ? (
          <section aria-label="Up next">
            <h3 className="co-review-section">Up next</h3>
            <ul className="co-review-rows">
              {upNext.map((p) => (
                <li key={p.id} className="co-review-row">
                  <div className="co-review-row-copy">
                    <span className="co-review-row-title" dir="auto">{titleOf(p)}</span>
                    <span className="co-review-path">{placementOf(p).path.join(" › ")}</span>
                  </div>
                  <button type="button" className="co-review-round" data-kind="accept" aria-label={`Accept ${titleOf(p)}`} onClick={() => run([p], "accept")}>
                    <Check aria-hidden size={15} />
                  </button>
                  <button type="button" className="co-review-round" aria-label={`Decline ${titleOf(p)}`} onClick={() => run([p], "decline")}>
                    <X aria-hidden size={15} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {decided.length > 0 ? (
          <section aria-label="Done">
            <h3 className="co-review-section">Done</h3>
            <ul className="co-review-rows">
              {decided.map(({ proposal: p, mark }) => (
                <li key={p.id} className="co-review-row" data-done>
                  <div className="co-review-row-copy">
                    <span className="co-review-row-title" dir="auto">{titleOf(p)}</span>
                    <span className="co-review-path">{placementOf(p).path.join(" › ")}</span>
                  </div>
                  <span className="co-review-done" data-mark={mark}>
                    {mark === "accept" ? <Check aria-hidden size={13} /> : <X aria-hidden size={13} />}
                    {mark === "accept" ? "Accepted" : "Declined"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      {open.length > 1 ? (
        <div className="co-review-bulk">
          <button type="button" className="co-review-text-btn" onClick={() => run(open, "decline")}>
            Decline all
          </button>
          <button type="button" className="co-review-btn co-glass-control" data-kind="glass" data-size="40" onClick={() => run(open, "accept")}>
            Accept all
          </button>
        </div>
      ) : null}
    </aside>
  );
}

/**
 * The pane runs from the end of the page header to the foot of the canvas and
 * out to the canvas's right edge, past main's gutter. The page's wrappers clip
 * their overflow, so it is fixed, and framed from the split and the canvas
 * (the .co-grid-field) on every resize.
 */
function usePaneFrame(active: boolean) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const split = el.parentElement;
    const field = el.closest<HTMLElement>(".co-grid-field") ?? document.documentElement;
    const place = () => {
      const s = split?.getBoundingClientRect();
      const f = field.getBoundingClientRect();
      el.style.top = `${Math.round(s?.top ?? f.top)}px`;
      el.style.bottom = `${Math.round(window.innerHeight - f.bottom)}px`;
      el.style.right = `${Math.round(window.innerWidth - f.right)}px`;
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(field);
    if (split) ro.observe(split);
    window.addEventListener("resize", place);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [active]);
  return ref;
}

/* ---- the current proposal (desk) ------------------------------------------------ */

type CardProps = {
  brandId: string;
  proposal: FactProposal;
  concepts: ConceptChipOption[];
  placement: ProposalPlacement;
  preview?: boolean;
  onDecide: (mark: Mark) => void;
  onAskAi: (instruction: string) => void;
};

function ProposalCard({ brandId, proposal, concepts, placement, preview, onDecide, onAskAi }: CardProps) {
  const [mode, setMode] = useState<"view" | "manual" | "ai">("view");
  const body = bodyOf(proposal);
  return (
    <article className="co-review-card" aria-label={titleOf(proposal)}>
      <span className="co-review-card-path">{placement.path.join(" › ")}</span>
      <h3 className="co-review-card-title" dir="auto">{titleOf(proposal)}</h3>
      {mode === "manual" ? (
        <ManualEdit brandId={brandId} proposal={proposal} concepts={concepts} preview={preview} onDone={() => setMode("view")} />
      ) : mode === "ai" ? (
        <AiEdit proposal={proposal} onCancel={() => setMode("view")} onSend={onAskAi} />
      ) : (
        <>
          {body ? <p className="co-review-card-body" dir="auto">{body}</p> : null}
          {placement.chartsEmpty ? (
            <span className="co-review-charts">
              <span aria-hidden className="co-review-charts-dot" />
              Charts an empty group
            </span>
          ) : null}
          <div className="co-review-actions">
            <button type="button" className="co-review-btn" data-kind="accept" onClick={() => onDecide("accept")}>
              <Check aria-hidden size={14} />
              Accept
            </button>
            <button type="button" className="co-review-btn co-glass-control" data-kind="glass" onClick={() => onDecide("decline")}>
              <X aria-hidden size={14} />
              Decline
            </button>
            <KitMenuTrigger>
              <KitPressable>
                <button type="button" className="co-review-btn co-glass-control" data-kind="glass" data-hug>
                  <Pencil aria-hidden size={14} />
                  Edit
                  <ChevronDown aria-hidden size={13} className="text-co-muted" />
                </button>
              </KitPressable>
              <KitMenuPopover>
                <KitMenu onAction={(key) => setMode(key === "ai" ? "ai" : "manual")}>
                  <KitMenuItem id="manual" icon={Pencil}>Edit manually</KitMenuItem>
                  <KitMenuItem id="ai" icon={Sparkles}>Edit with AI</KitMenuItem>
                </KitMenu>
              </KitMenuPopover>
            </KitMenuTrigger>
          </div>
        </>
      )}
    </article>
  );
}

/* ---- edit manually ------------------------------------------------------------- */

function ManualEdit({
  brandId,
  proposal,
  concepts,
  preview,
  onDone,
}: {
  brandId: string;
  proposal: FactProposal;
  concepts: ConceptChipOption[];
  preview?: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const edit = useEditFactProposal(brandId, proposal.sessionId);
  const [name, setName] = useState(proposal.factName ?? "");
  const [description, setDescription] = useState(proposal.factDescription ?? proposal.factText);
  const [competitor, setCompetitor] = useState(proposal.competitor ?? "");
  const [conceptIds, setConceptIds] = useState(proposal.conceptIds);
  const [error, setError] = useState<string | null>(null);
  const nameById = new Map(concepts.map((c) => [c.id, c.name]));

  const save = () => {
    const n = name.trim();
    const d = description.trim();
    if (!n) return setError("Name can't be empty.");
    if (!d) return setError("Description can't be empty.");
    const next = { factName: n, factDescription: d, conceptIds, competitor: competitor.trim() || null };
    onDone();
    if (preview) {
      queue.add({ title: "Preview: the edit was not saved", variant: "info" });
      return;
    }
    // Optimistic: the card shows the edit at once; only this proposal rolls back.
    const key = brandUniversePendingFactProposalsKey(brandId);
    const patch = (to: Partial<FactProposal>) =>
      qc.setQueryData<FactProposal[]>(key, (list) => list?.map((p) => (p.id === proposal.id ? { ...p, ...to } : p)));
    void qc.cancelQueries({ queryKey: key });
    patch(next);
    edit.mutate(
      { proposalId: proposal.id, ...next },
      {
        onError: () => {
          patch({
            factName: proposal.factName,
            factDescription: proposal.factDescription,
            conceptIds: proposal.conceptIds,
            competitor: proposal.competitor,
          });
          queue.add({ title: "Couldn't save your edit", description: "Try again.", variant: "error" });
        },
      },
    );
  };

  return (
    <div className="co-review-form">
      <label className="co-review-field">
        <span className="co-review-label">Name (3–5 words)</span>
        <input className="co-review-input co-input-own-focus" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label className="co-review-field">
        <span className="co-review-label">Description</span>
        <textarea className="co-review-input co-input-own-focus" data-multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="co-review-pair">
        <label className="co-review-field">
          <span className="co-review-label">Competitor (optional)</span>
          <input className="co-review-input co-input-own-focus" value={competitor} onChange={(e) => setCompetitor(e.target.value)} />
        </label>
        <div className="co-review-field">
          <span className="co-review-label" id={`concepts-${proposal.id}`}>Concepts</span>
          <DialogTrigger>
            <Pressable>
              <button type="button" className="co-review-input co-review-select" aria-labelledby={`concepts-${proposal.id}`}>
                <span className="truncate">{conceptIds.map((id) => nameById.get(id) ?? "").filter(Boolean).join(", ") || "None"}</span>
              </button>
            </Pressable>
            <KitPopover placement="bottom start" offset={6} className="w-72 p-3">
              <Dialog aria-label="Concepts" className="outline-none">
                <ConceptChipPicker options={concepts} value={conceptIds} onChange={setConceptIds} />
              </Dialog>
            </KitPopover>
          </DialogTrigger>
        </div>
      </div>
      {error ? <p className="co-review-error">{error}</p> : null}
      <div className="co-review-form-actions">
        <button type="button" className="co-review-btn" data-kind="text" onClick={onDone}>
          Cancel
        </button>
        <button type="button" className="co-review-btn" data-kind="accept" onClick={save}>
          <Check aria-hidden size={14} />
          Save
        </button>
      </div>
    </div>
  );
}

/* ---- edit with AI --------------------------------------------------------------- */

function AiEdit({ proposal, onCancel, onSend }: { proposal: FactProposal; onCancel: () => void; onSend: (text: string) => void }) {
  const [text, setText] = useState("");
  const body = bodyOf(proposal);
  const send = () => {
    const t = text.trim();
    if (t) onSend(t);
  };
  return (
    <div className="co-review-form">
      {body ? <p className="co-review-card-body" dir="auto">{body}</p> : null}
      <div className="co-review-field" data-kind="ai">
        <label className="co-review-field">
          <span className="co-review-label">What should the AI change about this fact?</span>
          <textarea
            className="co-review-input co-input-own-focus"
            data-multiline
            rows={3}
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
        </label>
        {/* Figma 8 Oct: the hint sits under its field. */}
        <p className="co-review-hint">
          <Info aria-hidden size={14} />
          Closes this review. The assistant revises it in the chat.
        </p>
      </div>
      <div className="co-review-form-actions">
        <button type="button" className="co-review-btn" data-kind="text" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="co-review-btn" data-kind="accept" disabled={!text.trim()} onClick={send}>
          <Sparkles aria-hidden size={14} />
          Send
        </button>
      </div>
    </div>
  );
}

/* ---- phone ------------------------------------------------------------------------ */

function ReviewSheet({
  open,
  onClose,
  onDecideAll,
  renderCard,
}: {
  open: FactProposal[];
  onClose: () => void;
  onDecideAll: (mark: Mark) => void;
  renderCard: (p: FactProposal) => ReactNode;
}) {
  const portal = useCoShellPortal();
  const [shown, close] = useSheetExit(onClose);
  return (
    <ModalOverlay
      isOpen={shown}
      isDismissable
      onOpenChange={(o) => {
        if (!o) close();
      }}
      UNSTABLE_portalContainer={portal ?? undefined}
      className="co-fact-sheet-scrim"
    >
      <Modal className="co-fact-sheet co-review-sheet">
        <Dialog aria-label="Review proposed facts" className="co-fact-sheet-dialog">
          <div className="co-fact-sheet-handle" aria-hidden />
          <div className="co-review-sheet-head">
            <h2 className="co-review-title" data-size="phone">Review proposed facts</h2>
            <KitIconButton icon={X} kind="close" size="xl" aria-label="Close review" onClick={close} />
          </div>
          <p className="co-review-note">Accept or decline each fact. Decisions apply immediately.</p>
          <div className="co-review-sheet-bar">
            <span className="co-review-remaining">{open.length} remaining</span>
            {open.length > 1 ? (
              <>
                <button type="button" className="co-review-btn" data-kind="text" data-size="32" onClick={() => onDecideAll("decline")}>
                  Decline all
                </button>
                <button type="button" className="co-review-btn co-glass-control" data-kind="glass" data-size="40" onClick={() => onDecideAll("accept")}>
                  Accept all
                </button>
              </>
            ) : null}
          </div>
          <div className="co-review-sheet-list">{open.map(renderCard)}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function PhoneProposal({ brandId, proposal, concepts, placement, preview, onDecide, onAskAi }: CardProps) {
  const [mode, setMode] = useState<"view" | "manual" | "ai">("view");
  const body = bodyOf(proposal);
  return (
    <article className="co-review-phone-card" aria-label={titleOf(proposal)}>
      <h3 className="co-review-phone-title" dir="auto">{titleOf(proposal)}</h3>
      {mode === "manual" ? (
        <ManualEdit brandId={brandId} proposal={proposal} concepts={concepts} preview={preview} onDone={() => setMode("view")} />
      ) : mode === "ai" ? (
        <AiEdit proposal={proposal} onCancel={() => setMode("view")} onSend={onAskAi} />
      ) : (
        <>
          {body ? <p className="co-review-phone-body" dir="auto">{body}</p> : null}
          <div className="co-review-phone-actions">
            <span className={cn("co-review-path", "co-review-phone-where")}>{placement.path.slice(0, 2).join(" · ")}</span>
            <KitIconButton icon={Check} size="xl" aria-label={`Accept ${titleOf(proposal)}`} iconClassName="text-co-accent" onClick={() => onDecide("accept")} />
            <KitIconButton icon={X} size="xl" aria-label={`Decline ${titleOf(proposal)}`} onClick={() => onDecide("decline")} />
            <KitMenuTrigger>
              <KitPressable>
                <KitIconButton icon={MoreHorizontal} size="xl" aria-label={`Edit ${titleOf(proposal)}`} />
              </KitPressable>
              <KitMenuPopover>
                <KitMenu onAction={(key) => setMode(key === "ai" ? "ai" : "manual")}>
                  <KitMenuItem id="manual" icon={Pencil}>Edit manually</KitMenuItem>
                  <KitMenuItem id="ai" icon={Sparkles}>Edit with AI</KitMenuItem>
                </KitMenu>
              </KitMenuPopover>
            </KitMenuTrigger>
          </div>
        </>
      )}
    </article>
  );
}
